import { HugeiconsIcon } from "@hugeicons/react";
import { RulerIcon } from "@hugeicons/core-free-icons";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Unit, VALID_PRECISIONS } from "@/lib/measure";
import { useUnitPreferencesStore } from "@/lib/stores/unit-preferences-store";
import { cn } from "@/lib/utils";

const UNIT_OPTIONS: ReadonlyArray<{ value: Unit; label: string }> = [
  { value: Unit.INCHES_FRACTIONAL, label: "Inches (fractional)" },
  { value: Unit.INCHES_DECIMAL, label: "Inches (decimal)" },
  { value: Unit.MILLIMETERS, label: "Millimeters" },
  { value: Unit.FEET_INCHES, label: "Feet & inches" },
];

const UNIT_SHORT: Record<Unit, string> = {
  [Unit.INCHES_FRACTIONAL]: "Inches",
  [Unit.INCHES_DECIMAL]: "Inches dec",
  [Unit.MILLIMETERS]: "mm",
  [Unit.FEET_INCHES]: "ft-in",
};

function precisionsFor(unit: Unit): string[] {
  // Preserve declaration order from VALID_PRECISIONS (Set is insertion-ordered).
  return [...VALID_PRECISIONS[unit]];
}

export function UnitToggle() {
  const unit = useUnitPreferencesStore((s) => s.unit);
  const precision = useUnitPreferencesStore((s) => s.precision[s.unit]);
  const setUnit = useUnitPreferencesStore((s) => s.setUnit);
  const setPrecision = useUnitPreferencesStore((s) => s.setPrecision);

  return (
    <Popover>
      <PopoverTrigger
        render={
          <Button
            variant="ghost"
            size="sm"
            className="h-8 gap-1.5 px-2 cursor-pointer text-xs font-medium"
            aria-label="Unit preferences"
          />
        }
      >
        <HugeiconsIcon icon={RulerIcon} size={16} />
        <span className="hidden sm:inline">{UNIT_SHORT[unit]}</span>
        <span className="hidden sm:inline text-muted-foreground">{precision}</span>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-60 gap-3 p-3">
        <div className="space-y-1.5">
          <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            Mode
          </div>
          <div className="flex flex-col gap-0.5">
            {UNIT_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => setUnit(o.value)}
                className={cn(
                  "flex items-center justify-between rounded-sm px-2 py-1.5 text-xs cursor-pointer transition-colors",
                  unit === o.value
                    ? "bg-accent text-accent-foreground"
                    : "hover:bg-accent/50",
                )}
              >
                <span>{o.label}</span>
                {unit === o.value && (
                  <span className="text-[10px] text-muted-foreground">
                    active
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-1.5">
          <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            Precision
          </div>
          <div className="flex flex-wrap gap-1">
            {precisionsFor(unit).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPrecision(unit, p)}
                className={cn(
                  "rounded-sm border px-2 py-1 text-xs cursor-pointer transition-colors",
                  precision === p
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-input hover:bg-accent/50",
                )}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
